/**
 * Piezas visuales compartidas de las pantallas de facturas
 * (Claude Design · FacturasEmitidas.dc.html + ArchivoHistorico.dc.html).
 * Solo presentacion: ninguna decide que datos se muestran.
 */
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@lib/utils/cn';
import { getInvoiceStatusColor } from '@components/ui/designTokens';

/**
 * Tokens del diseño, compartidos entre Facturas emitidas, Archivo histórico y
 * Clientes para que las pantallas usen la misma tarjeta, el mismo input y el
 * mismo boton en vez de copias que se van separando. Viven en
 * components/ui/designTokens.ts desde que Clientes los necesito; se
 * re-exportan aca para no cambiar los imports que ya existian.
 */
export { CARD, FIELD_LABEL, FIELD, CTA, GHOST, PAGE_BTN } from '@components/ui/designTokens';

/** Cabecera de una celda de tabla (mayusculas, tracking ancho). */
export const TH =
  'whitespace-nowrap border-b border-[#ececec] bg-secondary-50 px-3 py-3 text-left text-[11px] font-bold uppercase tracking-[0.16em] text-secondary-400 dark:border-secondary-700 dark:bg-secondary-900';
/** Celda de tabla estandar. */
export const TD = 'border-b border-[#f4f4f4] px-3 py-2.5 align-middle dark:border-secondary-700';
/** Altura unica de los controles de la fila de busqueda (input + botones). */
export const CONTROL_H = 'h-11';

/**
 * Icono de "info" para un campo de filtro: en hover/foco muestra en texto
 * simple (sin nombres de campo tecnicos, sin codigos) para que sirve ese
 * filtro.
 *
 * El tooltip se renderiza en un portal a `document.body` con `position:
 * fixed` calculado desde `getBoundingClientRect()`, no como hijo absoluto del
 * boton: el dropdown de filtros tiene `overflow-y-auto` (necesita scroll
 * propio si el contenido no entra en su alto maximo), y cualquier hijo
 * `absolute` que se saliera de esos limites quedaba recortado o se veia
 * "fuera" del marco con borde en degrade en vez de flotar prolijo sobre todo.
 * Con el portal, el tooltip no vive dentro de ese contenedor con scroll —
 * flota sobre TODA la pantalla, medido en pixeles reales, y nunca se corta.
 *
 * Sin `title` en el boton a proposito: con `title` puesto, el tooltip nativo
 * del navegador (el rectangulo plano sin estilo que tarda ~1s en aparecer) se
 * mostraba SUPERPUESTO al tooltip custom — dos tooltips con el mismo texto al
 * mismo tiempo. `aria-label` cubre la accesibilidad (lo anuncia un lector de
 * pantalla) sin disparar ningun tooltip nativo del browser.
 */
export function FieldInfo({ text }: { text: string }) {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const TOOLTIP_WIDTH = 208; // w-52
  const VIEWPORT_MARGIN = 10;

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, TOOLTIP_WIDTH / 2 + VIEWPORT_MARGIN),
      window.innerWidth - TOOLTIP_WIDTH / 2 - VIEWPORT_MARGIN
    );
    setCoords({ top: rect.bottom + 8, left });
  };
  const hide = () => setCoords(null);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        tabIndex={0}
        aria-label={text}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex shrink-0 cursor-help items-center justify-center align-middle text-[#6b239e] transition-colors hover:text-[#57197f] dark:text-[#b07fd9] dark:hover:text-[#cba7e8]"
      >
        <InformationCircleIcon className="h-3.5 w-3.5" />
      </button>
      {coords &&
        createPortal(
          <div
            role="tooltip"
            style={{ position: 'fixed', top: coords.top, left: coords.left, width: TOOLTIP_WIDTH, transform: 'translateX(-50%)' }}
            className="pointer-events-none z-[9999] rounded-lg bg-ink-850 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-white shadow-[0_8px_20px_-8px_rgba(10,34,51,.5)] dark:bg-secondary-700"
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
}

/** Cabecera de un grupo de filtros: icon-tile + label + linea. */
export function FilterGroup({
  icon,
  tone,
  title,
  children,
}: {
  icon: React.ReactNode;
  tone: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-md ${tone}`}>
          {icon}
        </span>
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink-850 dark:text-white">
          {title}
        </span>
        <span className="h-px flex-1 bg-[#f4f4f4] dark:bg-secondary-700" />
      </div>
      {children}
    </div>
  );
}

export function StatusChip({
  eInvoiceStatus,
  children,
}: {
  eInvoiceStatus: number;
  children: React.ReactNode;
}) {
  // Antes esto mezclaba, codigo por codigo, un fondo con `accent-500`
  // (variable de marca por TENANT, no un hex fijo) con un texto fijo
  // `#d17a12` — en un tenant cuyo color de marca no fuera anaranjado, el
  // fondo salia de un color y el texto de otro que no combinaba: el bug de
  // "los colores no salen bien". `getInvoiceStatusColor` resuelve siempre a
  // los 4 estados reales del negocio en hex fijo, los mismos que usan el
  // resumen y el filtro de Estado (designTokens.ts).
  const { bar, text } = getInvoiceStatusColor(eInvoiceStatus);
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full py-[3px] pl-2 pr-2.5 text-[12px] font-semibold"
      style={{ background: `${bar}1a`, color: text }}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: bar }} />
      {children}
    </span>
  );
}

/**
 * Tono del badge de tipo de documento. El diseño solo dibuja FI y FOI; el
 * resto de los 10 tipos que maneja la app reciben un tono coherente en vez de
 * quedarse sin estilo.
 */
const DOC_TONES: Record<string, string> = {
  default: 'bg-[#6b239e]/10 text-[#6b239e]',
  primary: 'bg-[#3a7ac7]/[.14] text-[#2f66b0]',
  info: 'bg-teal-500/10 text-[#0aa294]',
  success: 'bg-[#0d9488]/10 text-[#0d9488]',
  warning: 'bg-accent-500/10 text-[#d17a12]',
  danger: 'bg-[#b8503a]/[.14] text-[#a24030]',
};

/**
 * Filas "esqueleto" para la tabla de facturas mientras `isFetching` está en
 * true (búsqueda nueva, cambio de filtro, cambio de página) — mismas 7
 * columnas y anchos que el `<colgroup>` de MainPage/MinorPage, así el layout
 * no salta al pasar de esqueleto a datos reales.
 *
 * Reemplaza las filas en vez de solo atenuar las anteriores (`keepPreviousData`
 * seguía mostrando la página vieja sin ningún cambio visible salvo un spinner
 * chico junto a "Resultados") — se pidió una señal de carga más clara en la
 * tabla, sin bloquear toda la pantalla con el spinner grande de la carga
 * inicial.
 */
export function InvoiceRowsSkeleton({ rows = 10 }: { rows?: number }) {
  const bar = 'animate-pulse rounded-full bg-secondary-100 dark:bg-secondary-700';
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="last:[&>td]:border-b-0" aria-hidden="true">
          {/* Documento: número + CUFE + resolución */}
          <td className={TD}>
            <div className={`h-4 w-32 ${bar}`} />
            <div className={`mt-1.5 h-3 w-40 ${bar}`} />
            <div className={`mt-1 h-2.5 w-24 ${bar}`} />
          </td>
          {/* Cliente: nombre + RUC */}
          <td className={TD}>
            <div className={`h-4 w-28 ${bar}`} />
            <div className={`mt-1.5 h-3 w-16 ${bar}`} />
          </td>
          {/* Tienda · Pto */}
          <td className={`${TD} text-center`}>
            <div className={`mx-auto h-3.5 w-12 ${bar}`} />
          </td>
          {/* Estado (pill) */}
          <td className={TD}>
            <div className={`h-5 w-20 rounded-full ${bar}`} />
          </td>
          {/* Monto total */}
          <td className={`${TD} text-right`}>
            <div className={`ml-auto h-4 w-20 ${bar}`} />
          </td>
          {/* Emisión: fecha + hora */}
          <td className={TD}>
            <div className={`h-4 w-16 ${bar}`} />
            <div className={`mt-1.5 h-3 w-14 ${bar}`} />
          </td>
          {/* Acción (ver detalles) */}
          <td className={`${TD} text-right`}>
            <div className={`ml-auto h-8 w-8 rounded-lg ${bar}`} />
          </td>
        </tr>
      ))}
    </>
  );
}

export function DocTypeBadge({
  variant,
  title,
  children,
}: {
  variant: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.06em]',
        DOC_TONES[variant] ?? DOC_TONES.default
      )}
    >
      {children}
    </span>
  );
}
